import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, functions } from '../firebaseConfig';
import { doc, onSnapshot, collection, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { getUserLevel, getXpProgress, getXpForNextLevel } from '../utils/LevelSystem';
import { STokenLogo } from '../components/StreamDisplay';
import './Profile.css';

const NOTIF_ICONS = {
  withdrawal: '💸',
  deletion:   '🗑️',
  purchase:   '🛍️',
  signup:     '🆕',
};

function timeAgo(ts) {
  if (!ts) return '';
  const ms = ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
  const diff = Date.now() - ms;
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function Profile() {
  const [userData, setUserData]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [deletionState, setDeletion]    = useState(null); // null | 'confirm' | 'submitting' | 'done' | 'error'
  const [notifications, setNotifs]      = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameState, setUsernameState] = useState(null); // null | 'editing' | 'saving' | 'done' | { error }
  const navigate = useNavigate();
  const userRef = useRef(null);

  useEffect(() => {
    const user = auth.currentUser;
    userRef.current = user;
    if (!user) { setLoading(false); return; }

    const timeout = setTimeout(() => setLoading(false), 3000);

    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        clearTimeout(timeout);
        const data = snap.exists() ? snap.data() : {};
        setUserData(data);
        setLoading(false);

        // Subscribe to dev notifications once we know user is dev
        if (data?.isDev) {
          setNotifLoading(true);
          const q = query(
            collection(db, 'dev_notifications'),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          onSnapshot(q, (nSnap) => {
            setNotifs(nSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setNotifLoading(false);
          }, () => setNotifLoading(false));
        }
      },
      (e) => { clearTimeout(timeout); console.error('fetchUser:', e); setLoading(false); }
    );
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleDeletionRequest = useCallback(async () => {
    setDeletion('submitting');
    try {
      const fn = httpsCallable(functions, 'requestAccountDeletion');
      await fn();
      setDeletion('done');
    } catch (e) {
      console.error('requestAccountDeletion:', e);
      setDeletion('error');
    }
  }, []);

  const handleDismissNotif = async (id) => {
    try {
      await httpsCallable(functions, 'dismissNotification')({ notificationId: id });
    } catch (e) {
      console.error('dismissNotification:', e);
    }
  };

  const handleSetUsername = async () => {
    if (!usernameInput.trim()) return;
    setUsernameState('saving');
    try {
      await httpsCallable(functions, 'setUsername')({ username: usernameInput.trim() });
      setUsernameState('done');
      setTimeout(() => setUsernameState(null), 2000);
    } catch (e) {
      const raw = e.message?.replace('FirebaseError: ', '').replace(/\[.*?\]\s*/, '') || '';
      const errorMsg = raw.includes('USERNAME_LIMIT_REACHED')
        ? 'You have used all 3 username changes.'
        : raw || 'Something went wrong.';
      setUsernameState({ error: errorMsg });
    }
  };

  if (loading) return (
    <div className="page profile">
      <div className="profile__skeleton skeleton" style={{ height: 200 }} />
    </div>
  );

  const totalXp   = userData?.totalXp || 0;
  const level     = getUserLevel(totalXp);
  const progress  = getXpProgress(totalXp);
  const xpToNext  = getXpForNextLevel(totalXp);
  const streams   = userData?.totalStreams || 0;
  const dollarVal = (streams * 0.0003203463).toFixed(2);
  const isDev     = userData?.isDev;
  const unreadCount = notifications.filter(n => !n.read).length;
  const displayName = userData?.username || userData?.displayName || (isDev ? 'Dev' : 'Agent');

  return (
    <div className="page profile">
      <div className="grid-bg" aria-hidden="true" />

      {/* Avatar + identity */}
      <div className="profile__hero">
        <div className={`profile__avatar${userData?.activeBorder ? ` profile__avatar--border-${userData.activeBorder}` : ''}`}>
          <span className="profile__avatar-letter">
            {displayName[0].toUpperCase()}
          </span>
        </div>
        <div className="profile__identity">
          <h1 className="profile__name">{displayName}</h1>
          <p className="profile__email">{userData?.email}</p>
          {isDev && <span className="profile__dev-badge">DEV</span>}
        </div>
      </div>

      {/* Level card */}
      <div className="card card--brand profile__level">
        <div className="profile__level-header">
          <div className="profile__level-badge">
            <span className="profile__level-num">{level}</span>
          </div>
          <div>
            <div className="profile__level-label">Level {level}</div>
            <div className="profile__level-xp">
              {xpToNext.toLocaleString()} XP to next level
            </div>
          </div>
        </div>
        <div className="profile__xp-bar-track">
          <div
            className="profile__xp-bar-fill"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="profile__xp-footer">
          <span>{progress}% complete</span>
          <span>{totalXp.toLocaleString()} total XP</span>
        </div>
      </div>

      {/* Stats */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Stats</div>
      <div className="profile__stats-grid">
        <div className="card profile__stat">
          <STokenLogo size={22} />
          <div className="profile__stat-value">{streams.toLocaleString()}</div>
          <div className="profile__stat-label">Streams</div>
        </div>
        <div className="card profile__stat">
          <span className="profile__stat-icon">💵</span>
          <div className="profile__stat-value">${dollarVal}</div>
          <div className="profile__stat-label">Value</div>
        </div>
        <div className="card profile__stat">
          <span className="profile__stat-icon">🔥</span>
          <div className="profile__stat-value">{userData?.consecutiveLoginDays || 0}</div>
          <div className="profile__stat-label">Streak</div>
        </div>
        <div className="card profile__stat">
          <span className="profile__stat-icon">💸</span>
          <div className="profile__stat-value">{userData?.totalPayouts || 0}</div>
          <div className="profile__stat-label">Payouts</div>
        </div>
      </div>

      {/* Username */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Username</div>
      <div className="card profile__account">
        {usernameState === null && (() => {
          const changeCount = userData?.usernameChangeCount || 0;
          const locked = !isDev && changeCount >= 3;
          const changesLeft = isDev ? null : Math.max(0, 3 - changeCount);

          return (
            <div className="profile__account-row">
              <div>
                <div className="profile__account-key">
                  {userData?.username ? 'Your username' : 'No username set'}
                </div>
                {userData?.username && (
                  <div className="profile__account-val" style={{ marginTop: 2 }}>@{userData.username}</div>
                )}
                {!isDev && (
                  <div style={{ fontSize: 'var(--fs-xs)', color: locked ? 'var(--color-danger, #ef4444)' : 'var(--text-tertiary)', marginTop: 4 }}>
                    {locked ? 'No changes remaining' : `${changesLeft} change${changesLeft !== 1 ? 's' : ''} remaining`}
                  </div>
                )}
              </div>
              {!locked && (
                <button
                  className="btn btn--ghost"
                  style={{ fontSize: 'var(--fs-sm)', padding: '6px 14px' }}
                  onClick={() => {
                    setUsernameInput(userData?.username || '');
                    setUsernameState('editing');
                  }}
                >
                  {userData?.username ? 'Change' : 'Set Username'}
                </button>
              )}
            </div>
          );
        })()}

        {(usernameState === 'editing' || usernameState === 'saving' || (usernameState && usernameState.error)) && (
          <div className="profile__username-form">
            <input
              className="profile__username-input"
              placeholder="username (3–20 chars)"
              value={usernameInput}
              onChange={e => {
                setUsernameInput(e.target.value);
                if (usernameState?.error) setUsernameState('editing');
              }}
              maxLength={20}
              autoCapitalize="none"
            />
            {usernameState?.error && (
              <div className="profile__username-error">{usernameState.error}</div>
            )}
            <div className="profile__username-actions">
              <button className="btn btn--ghost" onClick={() => setUsernameState(null)}>Cancel</button>
              <button
                className="btn btn--primary"
                onClick={handleSetUsername}
                disabled={usernameState === 'saving' || !usernameInput.trim()}
              >
                {usernameState === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {usernameState === 'done' && (
          <div className="profile__account-row">
            <span className="profile__account-key" style={{ color: 'var(--brand)' }}>Username saved!</span>
          </div>
        )}
      </div>

      {/* Account info */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Account</div>
      <div className="card profile__account">
        <div className="profile__account-row">
          <span className="profile__account-key">Email</span>
          <span className="profile__account-val">{userData?.email}</span>
        </div>
        <div className="profile__account-row">
          <span className="profile__account-key">Member since</span>
          <span className="profile__account-val">
            {userData?.createdAt
              ? new Date(userData.createdAt.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : auth.currentUser?.metadata?.creationTime
                ? new Date(auth.currentUser.metadata.creationTime).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : '—'}
          </span>
        </div>
      </div>

      {/* Legal */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Legal</div>
      <div className="card profile__account">
        <Link to="/terms" className="profile__account-row profile__account-link">
          <span className="profile__account-key">Terms of Service</span>
          <span className="profile__account-arrow">›</span>
        </Link>
        <Link to="/privacy" className="profile__account-row profile__account-link">
          <span className="profile__account-key">Privacy Policy</span>
          <span className="profile__account-arrow">›</span>
        </Link>
        <Link to="/safety" className="profile__account-row profile__account-link">
          <span className="profile__account-key">Safety Policy</span>
          <span className="profile__account-arrow">›</span>
        </Link>
      </div>

      {/* Account Deletion request */}
      <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>Danger Zone</div>
      <div className="card profile__account">
        {deletionState === 'done' ? (
          <div className="profile__deletion-pending">
            <span className="profile__deletion-icon">✓</span>
            <div>
              <div className="profile__deletion-title">Request Submitted</div>
              <div className="profile__deletion-sub">Our team has been notified and will contact you at {userData?.email} within 48 hours to process your deletion request.</div>
            </div>
          </div>
        ) : deletionState === 'confirm' || deletionState === 'submitting' ? (
          <div className="profile__deletion-confirm">
            <div className="profile__deletion-title">Request Account Deletion?</div>
            <div className="profile__deletion-sub">This will submit a deletion request to our support team. Your account will remain active until the request is processed.</div>
            <div className="profile__deletion-actions">
              <button className="btn btn--ghost" onClick={() => setDeletion(null)} disabled={deletionState === 'submitting'}>Cancel</button>
              <button className="btn btn--danger" onClick={handleDeletionRequest} disabled={deletionState === 'submitting'}>
                {deletionState === 'submitting' ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        ) : (
          <button
            className="profile__account-row profile__account-link profile__deletion-btn"
            onClick={() => setDeletion('confirm')}
          >
            <span className="profile__account-key" style={{ color: 'var(--color-danger, #ef4444)' }}>Request Account Deletion</span>
            <span className="profile__account-arrow">›</span>
          </button>
        )}
        {deletionState === 'error' && (
          <div className="profile__deletion-sub" style={{ color: 'var(--color-danger, #ef4444)', padding: '0 var(--sp-4) var(--sp-3)' }}>
            Something went wrong. Please email support@streamstack.us.
          </div>
        )}
      </div>

      {/* ── Dev Notifications (only visible to dev account) ── */}
      {isDev && (
        <>
          <div className="section-title" style={{ marginTop: 'var(--sp-5)' }}>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span className="profile__notif-badge">{unreadCount}</span>
            )}
          </div>
          <div className="profile__notif-list">
            {notifLoading && (
              <div className="card profile__notif-empty">Loading…</div>
            )}
            {!notifLoading && notifications.length === 0 && (
              <div className="card profile__notif-empty">No notifications yet.</div>
            )}
            {!notifLoading && notifications.map(n => (
              <div
                key={n.id}
                className={`card profile__notif-card${n.read ? ' profile__notif-card--read' : ''}`}
              >
                <div className="profile__notif-top">
                  <span className="profile__notif-icon">
                    {NOTIF_ICONS[n.data?.type] || '🔔'}
                  </span>
                  <div className="profile__notif-content">
                    <div className="profile__notif-title">{n.title}</div>
                    <div className="profile__notif-body">{n.body}</div>
                  </div>
                  <span className="profile__notif-time">{timeAgo(n.createdAt)}</span>
                </div>
                {!n.read && (
                  <div className="profile__notif-actions">
                    <button
                      className="btn btn--ghost profile__notif-dismiss"
                      onClick={() => handleDismissNotif(n.id)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <button className="btn btn--danger btn--full" style={{ marginTop: 'var(--sp-5)' }} onClick={handleSignOut}>
        Sign Out
      </button>
    </div>
  );
}
